/** Utilitários compartilhados — Centro Operacional Enterprise */

export type OccupancyTier = 'low' | 'medium' | 'high' | 'critical';

/**
 * Classifica somente ocupação: carga observada / capacidade parametrizada.
 * Não usar para atraso, SLA ou gargalo de fluxo.
 * 0–50 Folga · >50–80 Normal · >80–100 Atenção · >100 Sobrecarga.
 * 100% é Atenção.
 */
export function getOccupancyTier(percent: number): OccupancyTier {
  if (percent <= 50) return 'low';
  if (percent <= 80) return 'medium';
  if (percent <= 100) return 'high';
  return 'critical';
}

export const occupancyTier = getOccupancyTier;

export const OCCUPANCY_STATUS_LABEL: Record<OccupancyTier, string> = {
  low: 'Folga',
  medium: 'Normal',
  high: 'Atenção',
  critical: 'Sobrecarga',
};

export const OCCUPANCY_TEXT_COLOR: Record<OccupancyTier, string> = {
  low: '#34D399',
  medium: '#60A5FA',
  high: '#FBBF24',
  critical: '#F87171',
};

export const OCCUPANCY_BADGE_CLASS: Record<OccupancyTier, { bg: string; text: string }> = {
  low: { bg: 'bg-emerald-100 dark:bg-emerald-500/15', text: 'text-emerald-700 dark:text-emerald-300' },
  medium: { bg: 'bg-sky-100 dark:bg-sky-500/15', text: 'text-sky-700 dark:text-sky-300' },
  high: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-300' },
  critical: { bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-300' },
};

export const OCCUPANCY_CELL: Record<OccupancyTier, string> = {
  low: 'bg-emerald-500/90 text-white',
  medium: 'bg-sky-500/90 text-white',
  high: 'bg-amber-400/95 text-slate-900',
  critical: 'bg-red-500/95 text-white',
};

export const OCCUPANCY_DOT: Record<OccupancyTier, string> = {
  low: '🟢',
  medium: '🔵',
  high: '🟡',
  critical: '🔴',
};

export const OCCUPANCY_LEGEND: { label: string; tier: OccupancyTier; range: string }[] = [
  { label: 'Folga', tier: 'low', range: '0–50%' },
  { label: 'Normal', tier: 'medium', range: '>50–80%' },
  { label: 'Atenção', tier: 'high', range: '>80–100%' },
  { label: 'Sobrecarga', tier: 'critical', range: '>100%' },
];

export type FlowHealth = 'healthy' | 'attention' | 'bottleneck';

export function flowHealthFromMetrics(
  wip: number,
  slaRate: number,
  isBottleneck: boolean,
  slaSampled = true,
): FlowHealth {
  if (wip <= 0) return 'healthy';
  if (!slaSampled) return wip >= 8 || isBottleneck ? 'attention' : 'healthy';
  if (slaRate < 85) return 'bottleneck';
  if (slaRate < 95 || wip >= 10 || isBottleneck) return 'attention';
  return 'healthy';
}

export const FLOW_HEALTH_BORDER: Record<FlowHealth, string> = {
  healthy: 'border-emerald-500/60 bg-emerald-500/[0.04]',
  attention: 'border-amber-500/60 bg-amber-500/[0.04]',
  bottleneck: 'border-red-500/70 bg-red-500/[0.06] ring-1 ring-red-500/20',
};

export const EXEC_HERO_CLASS =
  'rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-surface)] shadow-sm overflow-hidden';

export const EXEC_SECTION_LABEL =
  'text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--text-secondary)]';

export const EXEC_BLOCK_GAP = 'gap-3';
